/**
 * The form request page's behavior half: the shared flow (`useFormRequestFlow`,
 * shared-client-screens rule 22) on the website's `webFetch`, as the piece's
 * runtime. It reads the sent form by its link (no sign-in: the token is the
 * authority), keeps the answers as the client types and submits them, with
 * the same routes and field rules as the Mini App's FormRequestPage. A sent
 * form refreshes the website's account reads (F6); Try again reloads the page,
 * as the app's does (F3). Draws nothing.
 */
import { useCallback, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { DraftAnswers } from '@/hooks/forms/useFormRequestDraft';
import { useFormRequestFlow, type FormRequestFetcher } from '@/hooks/forms/useFormRequestFlow';
import { requestTypeLine } from '@/pages/FormRequestPage/formRequestParts';
import type { FieldError, FormDefinition, FormResponseInput } from '@/types/forms';

import { CLIENT_SCOPE, SiteFetchError, webFetch } from '../../api/session';
import { usePieceEmit, usePieceOptions, type PieceInput, type PieceRuntime } from '../../contract/runtime';
import { usePageMover } from '../../pageMover';
import { contract, type FormRequestActions, type FormRequestEvents, type FormRequestState } from './contract';

export interface FormRequestInput extends PieceInput<FormRequestEvents> {
  readonly requestToken: string;
}

export type FormRequestRuntime = PieceRuntime<typeof contract, FormRequestState, FormRequestActions> & {
  /** The form drawn, once read. */
  readonly form: FormDefinition | null;
  readonly serverErrors: FieldError[];
  /** Every change on the page, the whole answer set. */
  readonly answersChanged: (answers: DraftAnswers) => void;
};

/** The website's transport, its errors in the terms the shared flow reads. */
const fetcher: FormRequestFetcher = <T>(path: string, init?: RequestInit) =>
  webFetch<T>(path, init).catch((err: unknown) => {
    throw err instanceof SiteFetchError ? { status: err.status, code: err.code, details: { ...err.body } } : {};
  });

export function useFormRequest(input: FormRequestInput): FormRequestRuntime {
  const options = usePieceOptions(contract, input.options);
  const emit = usePieceEmit(input.onEvent);
  const navigate = usePageMover();
  const queryClient = useQueryClient();

  const flow = useFormRequestFlow({
    requestToken: input.requestToken,
    fetcher,
    onSubmitted: useCallback((form: FormDefinition) => {
      // Waiting on you reads the pending forms; the sent one leaves it now.
      void queryClient.invalidateQueries({ queryKey: CLIENT_SCOPE });
      emit('submitted', { formId: form.formId });
    }, [emit, queryClient]),
  });

  const { submit } = flow;
  const actions = useMemo<FormRequestActions>(
    () => ({
      submit: (responses) => submit(responses as FormResponseInput[]),
      retry: () => window.location.reload(),
      goHome: () => navigate('/'),
    }),
    [submit, navigate],
  );

  return {
    state: {
      status: flow.status,
      title: flow.form?.title ?? null,
      kind: flow.form ? requestTypeLine(flow.form.formType) : null,
      anonymous: flow.isAnonymous,
      submitFailed: flow.submitFailed,
    },
    actions,
    options,
    form: flow.form,
    serverErrors: flow.serverErrors,
    answersChanged: flow.answersChanged,
  };
}
